import { useState, useEffect } from 'react';
import { fetchArcgisFieldValues, inspectArcgisLayer, useDataSourceDetail } from '../../api/dataSources';
import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import {
  ArcgisInspectedLayer,
  DataSource,
  DataSourceFormData,
  FieldMapping,
  SourceType,
  AdapterType,
  CUSTOM_REALTIME_SOURCE_TYPES,
  REALTIME_SOURCE_TYPES,
  SOURCE_TYPE_LABELS,
  SourceFilter,
  CORRIDOR_SOURCE_TYPES,
  FILTERABLE_SOURCE_TYPES,
} from '../../types/dataSource';
import FieldMappingsEditor from './FieldMappingsEditor';
import SourceFiltersEditor from './SourceFiltersEditor';
import ShapefileUploader from './ShapefileUploader';
import TreatmentDateField, { TreatmentDateFieldState, EMPTY_DATE_FIELD } from './TreatmentDateField';
import RevegetationTypeMapping, {
  RevegetationTypeState,
  EMPTY_REVEGETATION_TYPE,
} from './RevegetationTypeMapping';
import { TREATMENT_TYPES, TREATMENT_CONFIGS, DEFAULT_TREATMENT_CONFIG } from '../../constants/treatmentTypes';

const ADAPTER_OPTIONS: { value: AdapterType; label: string }[] = [
  { value: 'arcgis_feature_server', label: 'ArcGIS Feature Server' },
  { value: 'arcgis_image_server', label: 'ArcGIS Image Server' },
  { value: 'shapefile', label: 'Shapefile' },
  { value: 'raster_with_lookup', label: 'Raster with Lookup' },
  { value: 'nafi_multi_shapefile', label: 'NAFI Multi-Shapefile' },
  { value: 'mapbox_vector_tiles', label: 'Mapbox Vector Tiles' },
  { value: 'overture', label: 'Overture' },
  { value: 'firms_hotspots', label: 'NASA FIRMS Hotspots' },
  { value: 'dea_hotspots', label: 'DEA Hotspots' },
  { value: 'xweather_lightning', label: 'Xweather Lightning' },
  { value: 'synthetic_lightning', label: 'Synthetic Lightning' },
  { value: 'nsw_rfs_hazards', label: 'NSW RFS Major Incidents' },
  { value: 'vic_emv_hazards', label: 'VIC Emergency Incidents & Warnings' },
  { value: 'qld_qfes_hazards', label: 'QLD QFES Bushfire Alerts' },
  { value: 'sa_cfs_hazards', label: 'SA CFS Current Incidents' },
  { value: 'wa_dfes_hazards', label: 'WA EmergencyWA Incidents & Warnings' },
  { value: 'act_esa_hazards', label: 'ACT ESA Current Incidents' },
];

const STANDARD_ADAPTERS: AdapterType[] = [
  'arcgis_feature_server',
  'arcgis_image_server',
  'shapefile',
  'raster_with_lookup',
  'nafi_multi_shapefile',
  'mapbox_vector_tiles',
  'overture',
];

const ADAPTERS_BY_SOURCE_TYPE: Record<SourceType, AdapterType[]> = {
  fire_history: STANDARD_ADAPTERS,
  vegetation: STANDARD_ADAPTERS,
  asset: STANDARD_ADAPTERS,
  treatment: STANDARD_ADAPTERS,
  // Vector layers only: lines are buffered to a width, polygons cut as-is.
  roads: ['arcgis_feature_server', 'shapefile', 'mapbox_vector_tiles'],
  // Polygon layers (cadastre / parcels): an ArcGIS feature layer or a shapefile.
  property_boundary: ['arcgis_feature_server', 'shapefile'],
  hotspot: ['firms_hotspots', 'dea_hotspots'],
  lightning: ['xweather_lightning', 'synthetic_lightning'],
  hazard: [
    'nsw_rfs_hazards',
    'vic_emv_hazards',
    'qld_qfes_hazards',
    'sa_cfs_hazards',
    'wa_dfes_hazards',
    'act_esa_hazards',
  ],
  // The organisation's own ArcGIS layer: the same adapter (and credentials)
  // as an init-time ArcGIS source; the source type picks the polling path.
  ignition: ['arcgis_feature_server'],
  burnt_area: ['arcgis_feature_server'],
};

/** Config keys the ArcGIS realtime form owns, so the Advanced Config JSON box
 *  keeps whatever else is in there without the two fighting over these. A key
 *  listed here with no field above would be deleted on every save, so
 *  `layer_id` and `mode` are deliberately absent — they live in Advanced
 *  Config. */
const ARCGIS_CONFIG_KEYS = [
  'layer_name',
  'time_field',
  'id_field',
  'title_field',
  'where',
  'position_uncertainty_m',
] as const;

const POLL_PRESETS: { value: number; label: string }[] = [
  { value: 60, label: '1 min' },
  { value: 300, label: '5 min' },
  { value: 900, label: '15 min' },
  { value: 3600, label: 'Hourly' },
];

const API_BASED_ADAPTERS: AdapterType[] = [
  'arcgis_feature_server',
  'arcgis_image_server',
  'mapbox_vector_tiles',
  'overture',
];

const FILE_BASED_ADAPTERS: AdapterType[] = [
  'shapefile',
  'raster_with_lookup',
  'nafi_multi_shapefile',
];

const LOOKUP_ADAPTERS: AdapterType[] = ['raster_with_lookup', 'arcgis_image_server'];

const ALL_SOURCE_TYPES = Object.keys(SOURCE_TYPE_LABELS) as SourceType[];

interface DataSourceFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: DataSourceFormData) => void;
  editingSource?: DataSource | null;
  defaultSourceType: SourceType;
  /** Source types offered in the picker — the tab's family (init-time types,
   *  or every realtime kind). Defaults to all of them. */
  allowedSourceTypes?: SourceType[];
  /** A reason a type cannot be chosen right now (e.g. a metered realtime
   *  kind the tenant is not enabled for); it stays listed but greyed out. */
  sourceTypeDisabledReason?: (type: SourceType) => string | null;
  isSubmitting?: boolean;
}

const EMPTY_FORM: DataSourceFormData = {
  name: '',
  source_type: 'fire_history',
  adapter_type: 'arcgis_feature_server',
  api_url: '',
  file_path: '',
  lookup_table_path: '',
  asset_type: '',
  config: {},
  credentials: {},
  field_mappings: [],
};

export default function DataSourceFormDialog({
  open,
  onClose,
  onSubmit,
  editingSource,
  defaultSourceType,
  allowedSourceTypes,
  sourceTypeDisabledReason,
  isSubmitting = false,
}: DataSourceFormDialogProps) {
  const [form, setForm] = useState<DataSourceFormData>({ ...EMPTY_FORM, source_type: defaultSourceType });
  const [showCredentials, setShowCredentials] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [configJson, setConfigJson] = useState('{}');
  const [defaultTreatmentType, setDefaultTreatmentType] = useState(TREATMENT_TYPES[0]);
  const [startDateField, setStartDateField] = useState<TreatmentDateFieldState>(EMPTY_DATE_FIELD);
  const [endDateField, setEndDateField] = useState<TreatmentDateFieldState>(EMPTY_DATE_FIELD);
  const [vegDateField, setVegDateField] = useState<TreatmentDateFieldState>(EMPTY_DATE_FIELD);
  const [treatmentResidualFuel, setTreatmentResidualFuel] = useState('');
  // Revegetation: the type every feature gets (config.default_veg_class), or
  // per feature from an attribute (veg_class field mapping + config.veg_class_map).
  const [treatmentVegClass, setTreatmentVegClass] = useState('');
  const [revegType, setRevegType] = useState<RevegetationTypeState>(EMPTY_REVEGETATION_TYPE);
  const [treatmentElevatedFuelPct, setTreatmentElevatedFuelPct] = useState('');
  const [treatmentSurfaceFuelPct, setTreatmentSurfaceFuelPct] = useState('');
  const [treatmentRemoveSlash, setTreatmentRemoveSlash] = useState(false);
  // Attribute filters + corridor width for line features (config.filters,
  // config.line_width_m, config.width_column); also drive road stamping.
  const [treatmentFilters, setTreatmentFilters] = useState<SourceFilter[]>([]);
  const [treatmentLineWidth, setTreatmentLineWidth] = useState('');
  const [treatmentWidthColumn, setTreatmentWidthColumn] = useState('');
  const [pollPreset, setPollPreset] = useState<number | 'custom'>(300);
  const [customPollSeconds, setCustomPollSeconds] = useState('300');
  const [lookbackHours, setLookbackHours] = useState('24');
  const [retentionHours, setRetentionHours] = useState('72');
  const [realtimeEnabled, setRealtimeEnabled] = useState(true);
  // ArcGIS realtime layer settings.
  const [arcgisLayerName, setArcgisLayerName] = useState('');
  const [arcgisTimeField, setArcgisTimeField] = useState('');
  const [arcgisTitleField, setArcgisTitleField] = useState('');
  const [arcgisIdField, setArcgisIdField] = useState('');
  const [arcgisWhere, setArcgisWhere] = useState('');
  const [arcgisUncertainty, setArcgisUncertainty] = useState('');
  const [arcgisLayers, setArcgisLayers] = useState<ArcgisInspectedLayer[] | null>(null);
  const [arcgisTesting, setArcgisTesting] = useState(false);
  const [arcgisError, setArcgisError] = useState<string | null>(null);
  const [arcgisAuthenticated, setArcgisAuthenticated] = useState(false);
  const isBuiltin = editingSource?.is_builtin ?? false;
  const { dataSource: sourceDetail } = useDataSourceDetail(open && editingSource ? editingSource.id : null);

  useEffect(() => {
    if (editingSource) {
      const allMappings = editingSource.field_mappings ?? [];
      // Mappings the treatment form owns through its own controls; the
      // generic editor below shows the rest.
      const formOwnedLFs = ['start_date', 'end_date', 'veg_date', 'veg_class'];
      const nonDateMappings = allMappings.filter((m) => !formOwnedLFs.includes(m.logical_field));
      const cfg = editingSource.config ?? {};

      const findMapping = (lf: string) => allMappings.find((m) => m.logical_field === lf);
      const toDateState = (lf: string, configKey: string): TreatmentDateFieldState => {
        const m = findMapping(lf);
        if (m) return { mode: 'field', sourceColumn: m.source_column, dateFormat: m.date_format ?? '', fixedDate: '' };
        const fixed = cfg[configKey];
        if (fixed) return { mode: 'fixed', sourceColumn: '', dateFormat: '', fixedDate: String(fixed) };
        return EMPTY_DATE_FIELD;
      };

      setStartDateField(toDateState('start_date', 'default_start_date'));
      setEndDateField(toDateState('end_date', 'default_end_date'));
      setVegDateField(toDateState('veg_date', 'default_veg_date'));

      setForm({
        name: editingSource.name,
        source_type: editingSource.source_type,
        adapter_type: editingSource.adapter_type,
        api_url: editingSource.api_url ?? '',
        file_path: editingSource.file_path ?? '',
        lookup_table_path: editingSource.lookup_table_path ?? '',
        asset_type: editingSource.asset_type ?? '',
        config: editingSource.config ?? {},
        credentials: {},
        field_mappings: nonDateMappings,
      });
      const existingType = cfg.default_type;
      setDefaultTreatmentType(
        typeof existingType === 'string' && TREATMENT_TYPES.includes(existingType)
          ? existingType
          : TREATMENT_TYPES[0]
      );
      setTreatmentResidualFuel(cfg.default_residual_fuel != null ? String(cfg.default_residual_fuel) : '');
      setTreatmentVegClass(typeof cfg.default_veg_class === 'string' ? cfg.default_veg_class : '');
      const vegClassMapping = findMapping('veg_class');
      const vegClassMap = cfg.veg_class_map;
      setRevegType(
        vegClassMapping
          ? {
              mode: 'field',
              sourceColumn: vegClassMapping.source_column,
              rows:
                vegClassMap && typeof vegClassMap === 'object' && !Array.isArray(vegClassMap)
                  ? Object.entries(vegClassMap as Record<string, unknown>).map(([value, vegClass]) => ({
                      value,
                      vegClass: typeof vegClass === 'string' ? vegClass : '',
                    }))
                  : [],
            }
          : EMPTY_REVEGETATION_TYPE
      );
      setTreatmentElevatedFuelPct(cfg.default_elevated_fuel_reduction_pct != null ? String(cfg.default_elevated_fuel_reduction_pct) : '');
      setTreatmentSurfaceFuelPct(cfg.default_surface_fuel_reduction_pct != null ? String(cfg.default_surface_fuel_reduction_pct) : '');
      setTreatmentRemoveSlash(Boolean(cfg.default_remove_slash));
      setTreatmentFilters(
        Array.isArray(cfg.filters)
          ? (cfg.filters as unknown[])
              .filter((f): f is SourceFilter => !!f && typeof f === 'object' && 'column' in (f as object))
              .map((f) => ({ column: String(f.column ?? ''), op: f.op ?? 'eq', value: f.value == null ? '' : String(f.value) }))
          : []
      );
      setTreatmentLineWidth(cfg.line_width_m != null ? String(cfg.line_width_m) : '');
      setTreatmentWidthColumn(typeof cfg.width_column === 'string' ? cfg.width_column : '');
      const pollSeconds = typeof cfg.poll_interval_seconds === 'number' ? cfg.poll_interval_seconds : 300;
      setPollPreset(POLL_PRESETS.some((p) => p.value === pollSeconds) ? pollSeconds : 'custom');
      setCustomPollSeconds(String(pollSeconds));
      setLookbackHours(cfg.lookback_hours != null ? String(cfg.lookback_hours) : '24');
      setRetentionHours(cfg.retention_hours != null ? String(cfg.retention_hours) : '72');
      setRealtimeEnabled(cfg.enabled !== false);
      setArcgisLayerName(typeof cfg.layer_name === 'string' ? cfg.layer_name : '');
      setArcgisTimeField(typeof cfg.time_field === 'string' ? cfg.time_field : '');
      setArcgisTitleField(typeof cfg.title_field === 'string' ? cfg.title_field : '');
      setArcgisIdField(typeof cfg.id_field === 'string' ? cfg.id_field : '');
      setArcgisWhere(typeof cfg.where === 'string' ? cfg.where : '');
      setArcgisUncertainty(
        cfg.position_uncertainty_m != null ? String(cfg.position_uncertainty_m) : ''
      );
      setArcgisLayers(null);
      setArcgisError(null);
      setConfigJson(JSON.stringify(cfg, null, 2));
      setShowCredentials(editingSource.has_credentials);
    } else {
      setForm({
        ...EMPTY_FORM,
        source_type: defaultSourceType,
        adapter_type: ADAPTERS_BY_SOURCE_TYPE[defaultSourceType]?.[0] ?? EMPTY_FORM.adapter_type,
      });
      setDefaultTreatmentType(TREATMENT_TYPES[0]);
      setStartDateField(EMPTY_DATE_FIELD);
      setEndDateField(EMPTY_DATE_FIELD);
      setVegDateField(EMPTY_DATE_FIELD);
      setTreatmentResidualFuel('');
      setTreatmentVegClass('');
      setRevegType(EMPTY_REVEGETATION_TYPE);
      setTreatmentElevatedFuelPct('');
      setTreatmentSurfaceFuelPct('');
      setTreatmentRemoveSlash(false);
      setTreatmentFilters([]);
      setTreatmentLineWidth('');
      setTreatmentWidthColumn('');
      setPollPreset(300);
      setCustomPollSeconds('300');
      setLookbackHours('24');
      setRetentionHours('72');
      setRealtimeEnabled(true);
      setArcgisLayerName('');
      setArcgisTimeField('');
      setArcgisTitleField('');
      setArcgisIdField('');
      setArcgisWhere('');
      setArcgisUncertainty('');
      setArcgisLayers(null);
      setArcgisError(null);
      setArcgisAuthenticated(false);
      setConfigJson('{}');
      setShowCredentials(false);
      setShowAdvanced(false);
    }
  }, [editingSource, defaultSourceType, open]);

  useEffect(() => {
    if (sourceDetail?.credentials && Object.keys(sourceDetail.credentials).length > 0) {
      setForm((prev) => ({ ...prev, credentials: sourceDetail.credentials as DataSourceFormData['credentials'] }));
    }
  }, [sourceDetail]);

  const handleChange = (field: keyof DataSourceFormData, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSourceTypeChange = (sourceType: SourceType) => {
    setForm((prev) => {
      const validAdapters = ADAPTERS_BY_SOURCE_TYPE[sourceType];
      return {
        ...prev,
        source_type: sourceType,
        adapter_type: validAdapters.includes(prev.adapter_type) ? prev.adapter_type : validAdapters[0],
      };
    });
  };

  const handleCredentialChange = (field: string, value: string | number) => {
    setForm((prev) => ({
      ...prev,
      credentials: { ...prev.credentials, [field]: value },
    }));
  };

  const handleFieldMappingsChange = (mappings: FieldMapping[]) => {
    setForm((prev) => ({ ...prev, field_mappings: mappings }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let parsedConfig = form.config;
    try {
      parsedConfig = JSON.parse(configJson);
    } catch {
      // Keep existing config if JSON is invalid
    }

    let finalFieldMappings = form.field_mappings ?? [];

    if (form.source_type === 'treatment') {
      const typeConfig = TREATMENT_CONFIGS[defaultTreatmentType] || DEFAULT_TREATMENT_CONFIG;
      const treatmentFields: Record<string, unknown> = { default_type: defaultTreatmentType };
      if (typeConfig.showFuel && treatmentResidualFuel !== '') {
        treatmentFields.default_residual_fuel = parseFloat(treatmentResidualFuel);
      }
      const vegClassColumn = revegType.mode === 'field' ? revegType.sourceColumn.trim() : '';
      if (typeConfig.showVegClass) {
        if (treatmentVegClass) treatmentFields.default_veg_class = treatmentVegClass;
        if (vegClassColumn) {
          const vegClassMap: Record<string, string> = {};
          for (const row of revegType.rows) {
            const rowValue = row.value.trim();
            if (rowValue && row.vegClass) vegClassMap[rowValue] = row.vegClass;
          }
          treatmentFields.veg_class_map = vegClassMap;
        }
      }
      if (typeConfig.showThinningFields) {
        if (treatmentElevatedFuelPct !== '') treatmentFields.default_elevated_fuel_reduction_pct = parseFloat(treatmentElevatedFuelPct);
        if (treatmentSurfaceFuelPct !== '') treatmentFields.default_surface_fuel_reduction_pct = parseFloat(treatmentSurfaceFuelPct);
        treatmentFields.default_remove_slash = treatmentRemoveSlash;
      }

      const showStartDate = typeConfig.showStartDate;
      const showEndDate = !typeConfig.showVegDate;
      const showVegDate = typeConfig.showVegDate;

      if (showStartDate && startDateField.mode === 'fixed' && startDateField.fixedDate) {
        treatmentFields.default_start_date = startDateField.fixedDate;
      }
      if (showEndDate && endDateField.mode === 'fixed' && endDateField.fixedDate) {
        treatmentFields.default_end_date = endDateField.fixedDate;
      }
      if (showVegDate && vegDateField.mode === 'fixed' && vegDateField.fixedDate) {
        treatmentFields.default_veg_date = vegDateField.fixedDate;
      }

      // The form owns the vegetation-type keys: switching back to one type
      // for every feature must not leave a stale value map behind.
      const mergedTreatmentConfig: Record<string, unknown> = { ...parsedConfig, ...treatmentFields };
      if (!('default_veg_class' in treatmentFields)) delete mergedTreatmentConfig.default_veg_class;
      if (!('veg_class_map' in treatmentFields)) delete mergedTreatmentConfig.veg_class_map;
      parsedConfig = mergedTreatmentConfig;

      const dateMappings: FieldMapping[] = [];
      if (typeConfig.showVegClass && vegClassColumn) {
        dateMappings.push({ logical_field: 'veg_class', source_column: vegClassColumn });
      }
      if (showStartDate && startDateField.mode === 'field' && startDateField.sourceColumn) {
        dateMappings.push({ logical_field: 'start_date', source_column: startDateField.sourceColumn, date_format: startDateField.dateFormat || undefined });
      }
      if (showEndDate && endDateField.mode === 'field' && endDateField.sourceColumn) {
        dateMappings.push({ logical_field: 'end_date', source_column: endDateField.sourceColumn, date_format: endDateField.dateFormat || undefined });
      }
      if (showVegDate && vegDateField.mode === 'field' && vegDateField.sourceColumn) {
        dateMappings.push({ logical_field: 'veg_date', source_column: vegDateField.sourceColumn, date_format: vegDateField.dateFormat || undefined });
      }

      finalFieldMappings = [...finalFieldMappings, ...dateMappings];
    }

    if (FILTERABLE_SOURCE_TYPES.includes(form.source_type)) {
      // Attribute filters (treatments, roads, property boundaries) and, for
      // the corridor types, the line width. The form owns these keys: blank
      // clears them rather than leaving a stale value behind in Advanced
      // Config.
      const owned: Record<string, unknown> = { ...parsedConfig };
      const cleanFilters = treatmentFilters
        .map((f) => ({ ...f, column: f.column.trim(), value: (f.value ?? '').trim() }))
        .filter((f) => f.column !== '');
      if (cleanFilters.length > 0) owned.filters = cleanFilters;
      else delete owned.filters;
      if (CORRIDOR_SOURCE_TYPES.includes(form.source_type)) {
        const lineWidth = parseFloat(treatmentLineWidth);
        if (treatmentLineWidth !== '' && Number.isFinite(lineWidth) && lineWidth > 0) owned.line_width_m = lineWidth;
        else delete owned.line_width_m;
        if (treatmentWidthColumn.trim() !== '') owned.width_column = treatmentWidthColumn.trim();
        else delete owned.width_column;
      }
      parsedConfig = owned;
    }

    if (REALTIME_SOURCE_TYPES.includes(form.source_type)) {
      const pollSeconds = pollPreset === 'custom'
        ? Math.max(60, parseInt(customPollSeconds, 10) || 300)
        : pollPreset;
      parsedConfig = {
        ...parsedConfig,
        poll_interval_seconds: pollSeconds,
        lookback_hours: lookbackHours !== '' ? parseFloat(lookbackHours) : 24,
        retention_hours: retentionHours !== '' ? parseFloat(retentionHours) : 72,
        enabled: realtimeEnabled,
      };
    }

    if (
      form.adapter_type === 'arcgis_feature_server' &&
      CUSTOM_REALTIME_SOURCE_TYPES.includes(form.source_type)
    ) {
      // The form owns these keys: a blank field clears the key rather than
      // writing an empty string the adapter would then try to resolve.
      const arcgisFields: Record<string, unknown> = {
        layer_name: arcgisLayerName.trim(),
        time_field: arcgisTimeField.trim(),
        title_field: arcgisTitleField.trim(),
        id_field: arcgisIdField.trim(),
        where: arcgisWhere.trim(),
        position_uncertainty_m:
          arcgisUncertainty !== '' ? parseFloat(arcgisUncertainty) : '',
      };
      const merged: Record<string, unknown> = { ...parsedConfig };
      for (const key of ARCGIS_CONFIG_KEYS) {
        const value = arcgisFields[key];
        if (value === undefined || value === '' || Number.isNaN(value)) {
          delete merged[key];
        } else {
          merged[key] = value;
        }
      }
      parsedConfig = merged;
    }

    onSubmit({ ...form, config: parsedConfig, field_mappings: finalFieldMappings });
  };

  const isRealtime = REALTIME_SOURCE_TYPES.includes(form.source_type);
  const needsApiUrl = API_BASED_ADAPTERS.includes(form.adapter_type) || isRealtime;
  const needsFilePath = FILE_BASED_ADAPTERS.includes(form.adapter_type);
  const needsLookup = LOOKUP_ADAPTERS.includes(form.adapter_type);
  // An ArcGIS Feature Server source of a realtime kind is polled by the
  // realtime half of that adapter, which has its own settings below.
  const isArcgisRealtime =
    form.adapter_type === 'arcgis_feature_server' &&
    CUSTOM_REALTIME_SOURCE_TYPES.includes(form.source_type);
  // The same adapter pulling once at project init (treatments, assets, ...):
  // "Test connection" here proves the URL/credentials and discovers the
  // layer's fields for the filter and column pickers below.
  const isArcgisStatic = form.adapter_type === 'arcgis_feature_server' && !isArcgisRealtime;
  const needsCredentials =
    form.adapter_type === 'arcgis_feature_server' ||
    isArcgisRealtime ||
    form.adapter_type === 'firms_hotspots' ||
    form.adapter_type === 'xweather_lightning';
  // The layer the user picked (or the only one behind a layer URL), so the
  // field pickers can offer this layer's real columns.
  const arcgisLayer =
    arcgisLayers?.find(
      (l) => (l.name ?? '') === arcgisLayerName || arcgisLayers.length === 1
    ) ?? null;
  const arcgisDateFields = arcgisLayer?.date_fields ?? [];
  const arcgisFieldNames = (arcgisLayer?.fields ?? []).map((f) => f.name);

  const handleTestArcgis = async () => {
    setArcgisTesting(true);
    setArcgisError(null);
    try {
      const result = await inspectArcgisLayer({
        api_url: form.api_url,
        credentials: form.credentials,
        source_id: editingSource?.id,
        mode: isArcgisStatic ? 'static' : 'realtime',
      });
      setArcgisLayers(result.layers);
      setArcgisAuthenticated(result.authenticated);
      if (isArcgisStatic) {
        // A static source pulls one layer by URL: a service root with several
        // layers needs a pick (below), which then becomes the URL. A single
        // layer is adopted straight away so the fields show up.
        if (result.layers.length === 1) {
          setArcgisLayerName(result.layers[0].name ?? '');
          if (result.layers[0].url && result.layers[0].url !== form.api_url) {
            handleChange('api_url', result.layers[0].url);
          }
        } else {
          const current = result.layers.find((l) => l.url === form.api_url);
          setArcgisLayerName(current?.name ?? '');
        }
        return;
      }
      // One layer behind the URL, or a name already chosen: adopt its
      // suggested fields so the common case needs no typing at all.
      const chosen =
        result.layers.find((l) => (l.name ?? '') === arcgisLayerName) ??
        (result.layers.length === 1 ? result.layers[0] : null);
      if (chosen) {
        if (result.layers.length > 1) setArcgisLayerName(chosen.name ?? '');
        if (!arcgisTimeField && chosen.suggested?.time_field) {
          setArcgisTimeField(chosen.suggested.time_field);
        }
        if (!arcgisTitleField && chosen.suggested?.title_field) {
          setArcgisTitleField(chosen.suggested.title_field);
        }
        if (!editingSource && chosen.suggested_kind) {
          handleSourceTypeChange(chosen.suggested_kind);
        }
      }
    } catch (err) {
      setArcgisLayers(null);
      setArcgisError(
        (err as { message?: string })?.message ?? 'Could not reach the ArcGIS service'
      );
    } finally {
      setArcgisTesting(false);
    }
  };
  const adapterOptions = ADAPTER_OPTIONS.filter((opt) =>
    (ADAPTERS_BY_SOURCE_TYPE[form.source_type] ?? STANDARD_ADAPTERS).includes(opt.value)
  );
  const showAssetType = form.source_type === 'asset';
  const isCorridorSource = CORRIDOR_SOURCE_TYPES.includes(form.source_type);
  const treatmentTypeConfig = TREATMENT_CONFIGS[defaultTreatmentType] || DEFAULT_TREATMENT_CONFIG;

  // Attribute filters + line width, shared by treatment and roads sources.
  const corridorSettings = (
    <>
      <SourceFiltersEditor
        filters={treatmentFilters}
        onChange={setTreatmentFilters}
        disabled={isBuiltin}
        columnOptions={arcgisFieldNames}
      />

      <Divider />
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Linear Features</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1 }}>
        {form.source_type === 'roads'
          ? 'Lines (roads, tracks, trails) are buffered to this width and cut out of the vegetation layer. Polygons are cut as they are.'
          : 'Lines (tracks, trails, breaks) are buffered to this width when loaded as treatments or stamped into vegetation as roads. Polygons are unaffected.'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          fullWidth
          label="Width (m)"
          type="number"
          value={treatmentLineWidth}
          onChange={(e) => setTreatmentLineWidth(e.target.value)}
          inputProps={{ min: 0, step: 'any' }}
          helperText="Used when no width column applies (default 10 m)"
          disabled={isBuiltin}
        />
        <Autocomplete
          freeSolo
          fullWidth
          options={arcgisFieldNames}
          value={treatmentWidthColumn}
          onInputChange={(_e, value) => setTreatmentWidthColumn(value)}
          disabled={isBuiltin}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Width column"
              helperText="Optional: a numeric column holding each line's width in metres"
            />
          )}
        />
      </Box>
    </>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editingSource ? 'Edit Data Source' : 'Add Data Source'}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Name"
            value={form.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
          />

          <FormControl fullWidth>
            <InputLabel>Source Type</InputLabel>
            <Select
              value={form.source_type}
              label="Source Type"
              onChange={(e) => handleSourceTypeChange(e.target.value as SourceType)}
              disabled={!!editingSource}
            >
              {(allowedSourceTypes ?? ALL_SOURCE_TYPES).map((type) => {
                const reason = sourceTypeDisabledReason?.(type) ?? null;
                return (
                  <MenuItem key={type} value={type} disabled={reason !== null}>
                    {SOURCE_TYPE_LABELS[type]}
                    {reason ? ` — ${reason}` : ''}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Adapter Type</InputLabel>
            <Select
              value={form.adapter_type}
              label="Adapter Type"
              onChange={(e) => handleChange('adapter_type', e.target.value)}
            >
              {adapterOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {needsApiUrl && (
            <TextField
              fullWidth
              label={isArcgisRealtime ? 'Feature layer URL' : 'API URL'}
              value={form.api_url}
              onChange={(e) => {
                handleChange('api_url', e.target.value);
                setArcgisLayers(null);
              }}
              placeholder={
                isArcgisRealtime
                  ? 'https://services.arcgis.com/…/FeatureServer/0'
                  : undefined
              }
              helperText={
                isArcgisRealtime
                  ? 'The layer\'s REST URL (or the FeatureServer URL, then pick a layer below)'
                  : isRealtime
                  ? 'Leave blank for the provider default'
                  : undefined
              }
            />
          )}

          {isArcgisStatic && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleTestArcgis}
                  disabled={arcgisTesting || (!form.api_url && !form.credentials?.item_id && !editingSource)}
                >
                  {arcgisTesting ? 'Testing…' : 'Test connection'}
                </Button>
                {arcgisTesting && <CircularProgress size={18} />}
                {arcgisLayers && !arcgisTesting && (
                  <Typography variant="caption" color="text.secondary">
                    {arcgisLayers.length} layer{arcgisLayers.length === 1 ? '' : 's'} found
                    {arcgisAuthenticated ? ' (authenticated)' : ' (public access)'}
                  </Typography>
                )}
                {!arcgisLayers && !arcgisTesting && (
                  <Typography variant="caption" color="text.secondary">
                    Checks the URL and credentials and lists the layer&apos;s fields for the pickers below.
                  </Typography>
                )}
              </Box>

              {arcgisError && <Alert severity="error">{arcgisError}</Alert>}

              {arcgisLayers && arcgisLayers.length > 1 && (
                <FormControl fullWidth>
                  <InputLabel>Layer</InputLabel>
                  <Select
                    value={arcgisLayerName}
                    label="Layer"
                    onChange={(e) => {
                      const name = e.target.value as string;
                      setArcgisLayerName(name);
                      const layer = arcgisLayers.find((l) => (l.name ?? '') === name);
                      if (layer?.url) handleChange('api_url', layer.url);
                    }}
                  >
                    {arcgisLayers.map((l) => (
                      <MenuItem key={l.url} value={l.name ?? ''}>
                        {l.name ?? l.url}
                        {l.geometry_type ? ` — ${l.geometry_type.replace('esriGeometry', '')}` : ''}
                        {l.feature_count !== undefined ? ` (${l.feature_count} features)` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {arcgisLayer && (
                <Alert severity={arcgisLayer.problems?.length ? 'warning' : 'success'}>
                  {arcgisLayer.problems?.length ? (
                    arcgisLayer.problems.map((p) => <div key={p}>{p}</div>)
                  ) : (
                    <>
                      {arcgisLayer.name ?? 'Layer'}
                      {arcgisLayer.geometry_type
                        ? ` · ${arcgisLayer.geometry_type.replace('esriGeometry', '')}`
                        : ''}
                      {arcgisLayer.feature_count !== undefined
                        ? ` · ${arcgisLayer.feature_count} features`
                        : ''}
                      {` · ${arcgisFieldNames.length} fields`}
                      {arcgisLayer.geometry_type === 'esriGeometryPolyline' && isCorridorSource
                        ? '. Lines are buffered to the width set under Linear Features.'
                        : ''}
                    </>
                  )}
                </Alert>
              )}
            </>
          )}

          {isArcgisRealtime && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleTestArcgis}
                  disabled={arcgisTesting || !form.api_url}
                >
                  {arcgisTesting ? 'Testing…' : 'Test connection'}
                </Button>
                {arcgisTesting && <CircularProgress size={18} />}
                {arcgisLayers && !arcgisTesting && (
                  <Typography variant="caption" color="text.secondary">
                    {arcgisLayers.length} layer{arcgisLayers.length === 1 ? '' : 's'} found
                    {arcgisAuthenticated ? ' (authenticated)' : ' (public access)'}
                  </Typography>
                )}
              </Box>

              {arcgisError && <Alert severity="error">{arcgisError}</Alert>}

              {arcgisLayers && arcgisLayers.length > 1 && (
                <FormControl fullWidth>
                  <InputLabel>Layer</InputLabel>
                  <Select
                    value={arcgisLayerName}
                    label="Layer"
                    onChange={(e) => {
                      const name = e.target.value as string;
                      setArcgisLayerName(name);
                      const layer = arcgisLayers.find((l) => (l.name ?? '') === name);
                      setArcgisTimeField(layer?.suggested?.time_field ?? '');
                      setArcgisTitleField(layer?.suggested?.title_field ?? '');
                      if (!editingSource && layer?.suggested_kind) {
                        handleSourceTypeChange(layer.suggested_kind);
                      }
                    }}
                  >
                    {arcgisLayers.map((l) => (
                      <MenuItem key={l.url} value={l.name ?? ''}>
                        {l.name ?? l.url}
                        {l.geometry_type ? ` — ${l.geometry_type.replace('esriGeometry', '')}` : ''}
                        {l.feature_count !== undefined ? ` (${l.feature_count} features)` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {arcgisLayer?.problems?.length ? (
                <Alert severity="warning">
                  {arcgisLayer.problems.map((p) => (
                    <div key={p}>{p}</div>
                  ))}
                </Alert>
              ) : null}

              {arcgisLayer?.suggested_kind &&
                arcgisLayer.suggested_kind !== form.source_type && (
                  <Alert severity="info">
                    This layer publishes{' '}
                    {arcgisLayer.geometry_type?.replace('esriGeometry', '').toLowerCase()}{' '}
                    features, which usually means{' '}
                    {arcgisLayer.suggested_kind === 'burnt_area'
                      ? 'Burnt Areas'
                      : 'Ignition Points'}
                    .
                  </Alert>
                )}
            </>
          )}

          {isRealtime && (
            <>
              <Divider />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Realtime Settings</Typography>

              <FormControl fullWidth>
                <InputLabel>Poll Frequency</InputLabel>
                <Select
                  value={pollPreset}
                  label="Poll Frequency"
                  onChange={(e) => setPollPreset(e.target.value as number | 'custom')}
                >
                  {POLL_PRESETS.map((p) => (
                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                  ))}
                  <MenuItem value="custom">Custom</MenuItem>
                </Select>
              </FormControl>

              {pollPreset === 'custom' && (
                <TextField
                  fullWidth
                  label="Poll Interval (seconds)"
                  type="number"
                  value={customPollSeconds}
                  onChange={(e) => setCustomPollSeconds(e.target.value)}
                  inputProps={{ min: 60, step: 1 }}
                  helperText="Minimum 60 seconds"
                />
              )}

              {form.adapter_type === 'xweather_lightning' && (
                <Typography variant="caption" color="text.secondary">
                  Each poll first asks Xweather&apos;s 1-access summary whether any strike
                  hit each 100 km coverage disk since the last poll, and only then fetches
                  strike positions (10 accesses). Keep the poll at 5 minutes: Xweather
                  serves only the last ~5 minutes of positions and cannot backfill gaps.
                </Typography>
              )}

              <TextField
                fullWidth
                label="Lookback Hours"
                type="number"
                value={lookbackHours}
                onChange={(e) => setLookbackHours(e.target.value)}
                inputProps={{ min: 1, step: 'any' }}
                helperText={
                  form.adapter_type === 'xweather_lightning'
                    ? 'Not used by this source — the provider only ever returns the last ~5 minutes'
                    : 'How far back each poll looks for detections'
                }
              />

              <TextField
                fullWidth
                label="Retention Hours"
                type="number"
                value={retentionHours}
                onChange={(e) => setRetentionHours(e.target.value)}
                inputProps={{ min: 1, step: 'any' }}
                helperText="Detections older than this are purged"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={realtimeEnabled}
                    onChange={(e) => setRealtimeEnabled(e.target.checked)}
                  />
                }
                label="Polling Enabled"
              />

              {isArcgisRealtime && (
                <>
                  <Divider />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Layer Fields
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Leave these blank to let the poller choose: it uses the layer&apos;s
                    editor-tracking edit date and its GlobalID. Test the connection above
                    to pick from the layer&apos;s real fields.
                  </Typography>

                  {arcgisDateFields.length > 0 ? (
                    <FormControl fullWidth>
                      <InputLabel>Date Field</InputLabel>
                      <Select
                        value={arcgisTimeField}
                        label="Date Field"
                        onChange={(e) => setArcgisTimeField(e.target.value as string)}
                      >
                        <MenuItem value="">Choose automatically</MenuItem>
                        {arcgisDateFields.map((name) => (
                          <MenuItem key={name} value={name}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      label="Date Field"
                      value={arcgisTimeField}
                      onChange={(e) => setArcgisTimeField(e.target.value)}
                      helperText="Each poll asks for features edited at or after the last one"
                    />
                  )}

                  {arcgisFieldNames.length > 0 ? (
                    <FormControl fullWidth>
                      <InputLabel>Title Field</InputLabel>
                      <Select
                        value={arcgisTitleField}
                        label="Title Field"
                        onChange={(e) => setArcgisTitleField(e.target.value as string)}
                      >
                        <MenuItem value="">Choose automatically</MenuItem>
                        {arcgisFieldNames.map((name) => (
                          <MenuItem key={name} value={name}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      label="Title Field"
                      value={arcgisTitleField}
                      onChange={(e) => setArcgisTitleField(e.target.value)}
                      helperText="Shown as each detection's name on the map"
                    />
                  )}

                  <TextField
                    fullWidth
                    label="Filter (SQL where clause)"
                    value={arcgisWhere}
                    onChange={(e) => setArcgisWhere(e.target.value)}
                    placeholder="Status = 'Active'"
                    helperText="Optional — only features matching this are ingested"
                  />

                  {form.source_type === 'ignition' && (
                    <TextField
                      fullWidth
                      label="Position Uncertainty (m)"
                      type="number"
                      value={arcgisUncertainty}
                      onChange={(e) => setArcgisUncertainty(e.target.value)}
                      inputProps={{ min: 0, step: 'any' }}
                      helperText="How far the real fire may be from the reported point (default 250 m)"
                    />
                  )}
                </>
              )}
            </>
          )}

          {needsFilePath && form.adapter_type === 'shapefile' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <ShapefileUploader onUploaded={(path) => handleChange('file_path', path)} />
              {form.file_path && (
                <TextField
                  fullWidth
                  label="File Path (S3)"
                  value={form.file_path}
                  InputProps={{ readOnly: true }}
                  size="small"
                  helperText="Set automatically after upload. You may also enter a path manually."
                  onChange={(e) => handleChange('file_path', e.target.value)}
                />
              )}
            </Box>
          )}

          {needsFilePath && form.adapter_type !== 'shapefile' && (
            <TextField
              fullWidth
              label="File Path"
              value={form.file_path}
              onChange={(e) => handleChange('file_path', e.target.value)}
            />
          )}

          {needsLookup && (
            <TextField
              fullWidth
              label="Lookup Table Path"
              value={form.lookup_table_path}
              onChange={(e) => handleChange('lookup_table_path', e.target.value)}
            />
          )}

          {showAssetType && (
            <TextField
              fullWidth
              label="Asset Type"
              value={form.asset_type}
              onChange={(e) => handleChange('asset_type', e.target.value)}
            />
          )}

          {form.source_type === 'treatment' && (
            <>
              <FormControl fullWidth>
                <InputLabel>Default Treatment Type</InputLabel>
                <Select
                  value={defaultTreatmentType}
                  label="Default Treatment Type"
                  onChange={(e) => setDefaultTreatmentType(e.target.value)}
                >
                  {TREATMENT_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {treatmentTypeConfig.showFuel && (
                <TextField
                  fullWidth
                  label="Default Residual Fuel (t/ha)"
                  type="number"
                  value={treatmentResidualFuel}
                  onChange={(e) => setTreatmentResidualFuel(e.target.value)}
                  inputProps={{ min: 0, step: 'any' }}
                />
              )}

              {treatmentTypeConfig.showVegClass && (
                <RevegetationTypeMapping
                  value={revegType}
                  onChange={setRevegType}
                  defaultVegClass={treatmentVegClass}
                  onDefaultVegClassChange={setTreatmentVegClass}
                  columnOptions={arcgisFieldNames}
                  canLoadValues={isArcgisStatic && arcgisLayer !== null}
                  loadValues={(field) =>
                    fetchArcgisFieldValues({
                      api_url: form.api_url,
                      credentials: form.credentials,
                      source_id: editingSource?.id,
                      field,
                    })
                  }
                  disabled={isBuiltin}
                />
              )}

              {treatmentTypeConfig.showThinningFields && (
                <>
                  <TextField
                    fullWidth
                    label="Default Elevated Fuel Reduction %"
                    type="number"
                    value={treatmentElevatedFuelPct}
                    onChange={(e) => setTreatmentElevatedFuelPct(e.target.value)}
                    inputProps={{ min: 0, max: 100, step: 1 }}
                  />
                  <TextField
                    fullWidth
                    label="Default Surface Fuel Reduction %"
                    type="number"
                    value={treatmentSurfaceFuelPct}
                    onChange={(e) => setTreatmentSurfaceFuelPct(e.target.value)}
                    inputProps={{ min: 0, max: 100, step: 1 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={treatmentRemoveSlash}
                        onChange={(e) => setTreatmentRemoveSlash(e.target.checked)}
                      />
                    }
                    label="Default: Remove Slash"
                  />
                </>
              )}

              <Divider />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Date Configuration</Typography>

              {treatmentTypeConfig.showStartDate && (
                <TreatmentDateField
                  label="Start Date"
                  logicalField="start_date"
                  value={startDateField}
                  onChange={setStartDateField}
                  disabled={isBuiltin}
                />
              )}

              {!treatmentTypeConfig.showVegDate && (
                <TreatmentDateField
                  label="End Date"
                  logicalField="end_date"
                  value={endDateField}
                  onChange={setEndDateField}
                  disabled={isBuiltin}
                />
              )}

              {treatmentTypeConfig.showVegDate && (
                <TreatmentDateField
                  label="Revegetation Date"
                  logicalField="veg_date"
                  value={vegDateField}
                  onChange={setVegDateField}
                  disabled={isBuiltin}
                />
              )}

              <Divider />
              {corridorSettings}
            </>
          )}

          {form.source_type === 'roads' && (
            <>
              <Alert severity="info">
                Roads sources are cut out of the vegetation layer as non-burnable
                corridors. Pick one under &quot;Stamp roads into vegetation&quot; when
                creating a project, or add it to an existing project from its
                Data Sources settings.
              </Alert>
              {corridorSettings}
            </>
          )}

          {form.source_type === 'property_boundary' && (
            <>
              <Alert severity="info">
                A property boundary source is a cadastre or parcel layer. Its polygons
                within the project area become the project&apos;s property boundaries —
                choose one or more in the Property Boundaries step when creating a
                project, or add one to an existing project from its Data Sources
                settings. Map the Name field below to label each property; other
                geometry types are skipped.
              </Alert>
              <SourceFiltersEditor
                filters={treatmentFilters}
                onChange={setTreatmentFilters}
                disabled={isBuiltin}
                columnOptions={arcgisFieldNames}
              />
            </>
          )}

          {/* Credentials section */}
          {needsCredentials && (
            <Box>
              <Button
                size="small"
                onClick={() => setShowCredentials(!showCredentials)}
                startIcon={showCredentials ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                Credentials
              </Button>
              <Collapse in={showCredentials}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1, pl: 2 }}>
                  {isArcgisRealtime && (
                    <Typography variant="caption" color="text.secondary">
                      Leave empty for a public layer. For a secured layer give either a
                      named-user login (username + password, and Portal URL for ArcGIS
                      Enterprise) or a long-lived token.
                    </Typography>
                  )}
                  {(form.adapter_type === 'arcgis_feature_server' || isArcgisRealtime) && (
                    <>
                      <TextField
                        fullWidth
                        label="Portal URL"
                        value={form.credentials?.portal_url ?? ''}
                        onChange={(e) => handleCredentialChange('portal_url', e.target.value)}
                        placeholder="https://www.arcgis.com"
                      />
                      <TextField
                        fullWidth
                        label="Username"
                        value={form.credentials?.username ?? ''}
                        onChange={(e) => handleCredentialChange('username', e.target.value)}
                      />
                      <TextField
                        fullWidth
                        label="Password"
                        type="password"
                        value={form.credentials?.password ?? ''}
                        onChange={(e) => handleCredentialChange('password', e.target.value)}
                      />
                      {isArcgisRealtime ? (
                        <TextField
                          fullWidth
                          label="Token (instead of a login)"
                          type="password"
                          value={form.credentials?.token ?? ''}
                          onChange={(e) => handleCredentialChange('token', e.target.value)}
                          helperText="A long-lived ArcGIS token or API key"
                        />
                      ) : (
                        <>
                          <TextField
                            fullWidth
                            label="Item ID"
                            value={form.credentials?.item_id ?? ''}
                            onChange={(e) => handleCredentialChange('item_id', e.target.value)}
                          />
                          <TextField
                            fullWidth
                            label="Layer Index"
                            type="number"
                            value={form.credentials?.layer_index ?? 0}
                            onChange={(e) => handleCredentialChange('layer_index', parseInt(e.target.value, 10) || 0)}
                          />
                        </>
                      )}
                    </>
                  )}
                  {form.adapter_type === 'firms_hotspots' && (
                    <TextField
                      fullWidth
                      label="FIRMS MAP_KEY"
                      type="password"
                      value={form.credentials?.map_key ?? ''}
                      onChange={(e) => handleCredentialChange('map_key', e.target.value)}
                    />
                  )}
                  {form.adapter_type === 'xweather_lightning' && (
                    <>
                      <TextField
                        fullWidth
                        label="Client ID"
                        value={form.credentials?.client_id ?? ''}
                        onChange={(e) => handleCredentialChange('client_id', e.target.value)}
                      />
                      <TextField
                        fullWidth
                        label="Client Secret"
                        type="password"
                        value={form.credentials?.client_secret ?? ''}
                        onChange={(e) => handleCredentialChange('client_secret', e.target.value)}
                      />
                    </>
                  )}
                </Box>
              </Collapse>
            </Box>
          )}

          {/* Field Mappings */}
          {form.source_type !== 'asset' && form.source_type !== 'roads' && !isRealtime && (
            <Box>
              <Divider sx={{ my: 1 }} />
              <FieldMappingsEditor
                sourceType={form.source_type}
                mappings={form.field_mappings ?? []}
                onChange={handleFieldMappingsChange}
                disabled={isBuiltin}
                columnOptions={arcgisFieldNames}
              />
              {isBuiltin && (
                <Typography variant="caption" color="text.secondary">
                  Field mappings for built-in sources are managed via the init_data script.
                </Typography>
              )}
            </Box>
          )}

          {/* Advanced config */}
          <Box>
            <Button
              size="small"
              onClick={() => setShowAdvanced(!showAdvanced)}
              startIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              Advanced Config
            </Button>
            <Collapse in={showAdvanced}>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  {isArcgisRealtime
                    ? 'JSON configuration for the remaining adapter keys (layer_id, date_literal, coverage, coverage_buffer_km, bbox). The fields above own layer_name, time_field, title_field, id_field, where and position_uncertainty_m.'
                    : form.adapter_type === 'xweather_lightning'
                    ? 'JSON configuration: summary_gate ("any" = fetch strikes when the summary counts any pulse, "cg" = cloud-to-ground only, "off" = always fetch), disk_radius_km (max 100), coverage_buffer_km (fixed buffer; default is each project\'s alert radius, min 10 km), max_disks, bbox.'
                    : isRealtime
                    ? 'JSON configuration for adapter-specific keys (dataset, type_name, bbox, coverage_buffer_km, seed, strikes_per_poll, etc.)'
                    : 'JSON configuration (extent_bbox, CRS overrides, out_fields, etc.)'}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  maxRows={12}
                  value={configJson}
                  onChange={(e) => setConfigJson(e.target.value)}
                  disabled={isBuiltin}
                  sx={{ fontFamily: 'monospace' }}
                />
              </Box>
            </Collapse>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={isSubmitting}>
          {editingSource ? 'Save' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
